/**
 * AutoAcer USA — Custom Product Search
 * Searches via backend API by OEM number, make, model, and product title
 *
 * CONFIG — update these two values:
 */
const AA_SEARCH_API   = 'https://api.autoacer.com/public/shopify/search';
const AA_COUNTRY_ID   = 2;   // UK country ID
const AA_SEARCH_KEY   = 'aa_srch_y1rwreg4_9mKpQvR3nZ7xLwT2';

;(function () {
  const MIN_CHARS   = 2;
  const DEBOUNCE_MS = 300;

  let debounceTimer = null;
  let activeIndex   = -1;
  let dropdown      = null;
  let lastInput     = null;

  // ── CSS ────────────────────────────────────────────────────────────────────
  const css = `
    .aa-dropdown {
      position: absolute;
      top: calc(100% + 4px);
      left: 0; right: 0;
      background: #fff;
      border: 1px solid #dde1ef;
      border-radius: 10px;
      box-shadow: 0 8px 32px rgba(32,41,93,0.14);
      z-index: 99999;
      display: none;
    }
    .aa-dropdown.open { display: block; }

    .aa-results {
      max-height: calc(4 * 110px);
      overflow-y: auto;
      scrollbar-width: thin;
      scrollbar-color: rgba(0,0,0,0.15) transparent;
    }
    .aa-results::-webkit-scrollbar { width: 4px; }
    .aa-results::-webkit-scrollbar-track { background: transparent; }
    .aa-results::-webkit-scrollbar-thumb { background: rgba(0,0,0,0.15); border-radius: 4px; }

    .aa-item {
      display: flex;
      align-items: flex-start;
      gap: 14px;
      padding: 10px 14px 10px 14px;
      border-bottom: 1px solid #f0f2f8;
      cursor: pointer;
      text-decoration: none;
      color: inherit;
      transition: background 0.12s;
      min-height: 90px;
    }
    .aa-item:last-of-type { border-bottom: none; }
    .aa-item:hover, .aa-item.active { background: #f7f8fc; }

    .aa-item img {
      width: 90px; height: 90px;
      object-fit: contain;
      border: 1px solid #eaecf3;
      border-radius: 6px;
      flex-shrink: 0;
      background: #fff;
    }
    .aa-item .no-img {
      width: 90px; height: 90px;
      background: #f0f2f8;
      border-radius: 6px;
      border: 1px solid #eaecf3;
      flex-shrink: 0;
    }
    .aa-item .info { flex: 1; min-width: 0; }
    .aa-item .sku {
      font-size: 12px; font-weight: 700; color: #20295D;
      display: block; margin-bottom: 3px; line-height: 1.3; text-align: left;
    }
    .aa-item:hover .sku, .aa-item.active .sku { color: #E8831A; }
    .aa-item .title {
      font-size: 13px; font-weight: 700; color: #20295D;
      white-space: normal; line-height: 1.4; margin: 0;
      overflow-wrap: break-word; text-align: left;
    }
    .aa-item:hover .title, .aa-item.active .title { color: #E8831A; }
    .aa-item .compatible {
      font-size: 11px; color: #aaa; font-style: italic; margin-top: 4px; text-align: left;
    }
    .aa-status {
      padding: 14px 16px; font-size: 13px; color: #888; text-align: center;
    }
    .aa-footer {
      padding: 10px 14px; border-top: 1px solid #f0f2f8; text-align: center;
    }
    .aa-footer a {
      font-size: 12px; font-weight: 700; color: #20295D; text-decoration: none;
    }
    .aa-footer a:hover { color: #E8831A; }
  `;
  const styleEl = document.createElement('style');
  styleEl.textContent = css;
  document.head.appendChild(styleEl);

  // ── Helpers ────────────────────────────────────────────────────────────────
  function esc(s) {
    return String(s || '')
      .replace(/&/g,'&amp;').replace(/</g,'&lt;')
      .replace(/>/g,'&gt;').replace(/"/g,'&quot;');
  }

  function isSearchInput(el) {
    return el && el.tagName === 'INPUT' &&
      (el.type === 'search' || el.name === 'q' || el.classList.contains('search__input'));
  }

  function getOrCreateDropdown(input) {
    // Re-use dropdown attached to this input
    if (input._aaDropdown) return input._aaDropdown;

    const parent = input.closest('.field, .search-modal__form, form') || input.parentElement;
    parent.style.position = 'relative';

    const dd = document.createElement('div');
    dd.className = 'aa-dropdown';
    parent.appendChild(dd);
    input._aaDropdown = dd;
    return dd;
  }

  function open(dd) {
    dd.classList.add('open');
    // Suppress Dawn's predictive search
    const predictive = document.querySelector('predictive-search');
    if (predictive) { predictive.removeAttribute('open'); predictive.removeAttribute('loading'); }
  }
  function close(dd) { dd.classList.remove('open'); activeIndex = -1; }

  function setActive(items) {
    items.forEach((el, i) => el.classList.toggle('active', i === activeIndex));
    if (activeIndex >= 0) items[activeIndex].scrollIntoView({ block: 'nearest' });
  }

  // ── Fetch ──────────────────────────────────────────────────────────────────
  async function doSearch(q, dd) {
    dd.innerHTML = '<div class="aa-status">Searching…</div>';
    open(dd);

    try {
      const url = `${AA_SEARCH_API}?q=${encodeURIComponent(q)}&countryId=${AA_COUNTRY_ID}&limit=10`;
      const res  = await fetch(url, { headers: { 'Accept': 'application/json', 'X-AA-Search-Key': AA_SEARCH_KEY } });
      if (!res.ok) throw new Error('API ' + res.status);
      const { results } = await res.json();
      render(results, dd, q);
    } catch (err) {
      console.warn('[AA Search]', err);
      dd.innerHTML = '<div class="aa-status">Search unavailable. Please try browsing collections.</div>';
    }
  }

  // ── OEM helper: extract string from object or string ──────────────────────
  function oemStrings(oem_nos) {
    if (!Array.isArray(oem_nos)) return [];
    return oem_nos.map(o => typeof o === 'string' ? o : (o && (o.title || o.value || o.oem_no || ''))).filter(Boolean);
  }

  // ── Render ─────────────────────────────────────────────────────────────────
  function render(results, dd, q) {
    activeIndex = -1;
    dd.innerHTML = '';

    if (!results.length) {
      dd.innerHTML = `<div class="aa-status">No results for "<b>${esc(q)}</b>"</div>`;
      open(dd);
      return;
    }

    const resultsWrap = document.createElement('div');
    resultsWrap.className = 'aa-results';

    results.forEach(item => {
      const a = document.createElement('a');
      a.className = 'aa-item';
      a.href = `/products/${item.handle}`;

      const oems = oemStrings(item.oem_nos);
      const qLower = q.toLowerCase();
      const matchingOem = oems.find(o => o.toLowerCase().includes(qLower));
      const otherOems   = oems.filter(o => o !== matchingOem).slice(0, 2);
      const oemParts    = matchingOem ? [matchingOem, ...otherOems].slice(0, 3) : otherOems.slice(0, 3);
      const oemText     = oemParts.join(', ');
      const metaParts   = [item.make, oemText].filter(Boolean);
      const meta        = metaParts.join(', ');

      a.innerHTML = `
        ${item.image
          ? `<img src="${esc(item.image)}" alt="${esc(item.title)}" loading="lazy">`
          : `<div class="no-img"></div>`}
        <div class="info">
          ${item.sku ? `<div class="sku">AA No. ${esc(item.sku)}</div>` : ''}
          <div class="title">${esc(item.title)}</div>
          ${meta ? `<div class="compatible">Compatible with: ${esc(meta)}</div>` : ''}
        </div>
      `;
      resultsWrap.appendChild(a);
    });

    dd.appendChild(resultsWrap);

    const footer = document.createElement('div');
    footer.className = 'aa-footer';
    footer.innerHTML = `<a href="/search?q=${encodeURIComponent(q)}&options%5Bprefix%5D=last">View all results &rarr;</a>`;
    dd.appendChild(footer);

    open(dd);
  }

  // ── Event delegation — capture phase catches it before predictive-search ──
  document.addEventListener('input', function (e) {
    const input = e.target;
    if (!isSearchInput(input)) return;

    lastInput = input;
    const q = input.value.trim();
    const dd = getOrCreateDropdown(input);

    // Hide Shopify's own predictive results
    const shopifyResults = document.getElementById('predictive-search-results');
    if (shopifyResults) shopifyResults.style.display = 'none';

    clearTimeout(debounceTimer);
    if (q.length < MIN_CHARS) { close(dd); return; }
    debounceTimer = setTimeout(() => doSearch(q, dd), DEBOUNCE_MS);
  }, true); // capture = true → fires before predictive-search element

  // ── Keyboard nav ──────────────────────────────────────────────────────────
  document.addEventListener('keydown', function (e) {
    const input = e.target;
    if (!isSearchInput(input) || !input._aaDropdown) return;
    const dd    = input._aaDropdown;
    const items = dd.querySelectorAll('.aa-results .aa-item');
    if (!items.length) return;

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      activeIndex = Math.min(activeIndex + 1, items.length - 1);
      setActive(items);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      activeIndex = Math.max(activeIndex - 1, 0);
      setActive(items);
    } else if (e.key === 'Enter' && activeIndex >= 0) {
      e.preventDefault();
      items[activeIndex].click();
    } else if (e.key === 'Escape') {
      close(dd);
    }
  }, true);

  // ── Close on outside click ─────────────────────────────────────────────────
  document.addEventListener('click', function (e) {
    if (!lastInput || !lastInput._aaDropdown) return;
    const dd = lastInput._aaDropdown;
    if (!dd.contains(e.target) && e.target !== lastInput) close(dd);
  });

  // ── Restore Shopify predictive results when input is cleared ──────────────
  document.addEventListener('input', function (e) {
    if (!isSearchInput(e.target)) return;
    if (e.target.value.trim().length === 0) {
      const shopifyResults = document.getElementById('predictive-search-results');
      if (shopifyResults) shopifyResults.style.display = '';
    }
  }, true);

  // ── Auto-focus search input when search modal opens ────────────────────────
  new MutationObserver(function (mutations) {
    mutations.forEach(function (m) {
      if (m.attributeName === 'open' && m.target.tagName === 'DETAILS' && m.target.hasAttribute('open')) {
        const input = m.target.querySelector('.search-modal__form input[type="search"], .search-modal__form input[name="q"]');
        if (input) setTimeout(() => input.focus(), 80);
      }
    });
  }).observe(document.documentElement, { subtree: true, attributeFilter: ['open'] });

})();
